import { useEffect, useState, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import api, { errorMessage } from "../lib/api";
import { PageHeader, PillBadge, GhostButton } from "../components/Primitives";
import { toast } from "sonner";
import { Eye, Trash2, User, MapPin, Calendar } from "lucide-react";
import { Modal } from "../components/Modal";
import StickyFiltersBar, { useFullscreenRoute } from "../components/StickyFiltersBar";
import { SortableHeader, useSortableRows } from "../components/SortableTable";
import { useRememberWindowGeometry } from "../lib/popoutGeometry";

export default function SupervisorVisits() {
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);
  const [filters, setFilters] = useState({ client_names: [], site_ids: [], guard_ids: [] });
  const location = useLocation();
  const isFs = useFullscreenRoute(location.pathname);
  useRememberWindowGeometry(isFs ? location.pathname : null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/supervisor-visits");
      setRows(r.data || []);
    } catch (e) { toast.error(errorMessage(e, "Load failed")); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this supervisor visit?")) return;
    try {
      await api.delete(`/supervisor-visits/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(errorMessage(e, "Delete failed")); }
  };

  // Client-side filter matches DAR: rows narrowed by client_names / site_ids
  // (site_id) / guard_ids (any guard_observed).
  const filtered = useMemo(() => {
    const { client_names, site_ids, guard_ids } = filters;
    if (!client_names.length && !site_ids.length && !guard_ids.length) return rows;
    const cset = new Set(client_names);
    const sset = new Set(site_ids);
    const gset = new Set(guard_ids);
    return rows.filter((v) => {
      if (cset.size > 0 && !cset.has(v.client_name)) return false;
      if (sset.size > 0 && !sset.has(v.site_id)) return false;
      if (gset.size > 0) {
        const ids = v.guards_observed || [];
        const hit = ids.some((g) => gset.has(g));
        if (!hit) return false;
      }
      return true;
    });
  }, [rows, filters]);

  const sortAccessors = useMemo(() => ({
    visit_time: (r) => r.visit_time || "",
    supervisor_name: (r) => (r.supervisor_name || "").toLowerCase(),
    site_name: (r) => (r.site_name || "").toLowerCase(),
    guards_count: (r) => (r.guards_observed || []).length,
    findings_count: (r) => (r.findings_tags || []).length,
  }), []);
  const sortState = useSortableRows(filtered, sortAccessors, { defaultKey: "visit_time", defaultDir: "desc", storageKey: "supervisor-visits" });

  const containerClass = isFs ? "fixed inset-0 z-50 bg-[#0A0A0A] overflow-auto" : "";

  return (
    <div className={containerClass}>
      {!isFs && (
        <PageHeader
          title="Supervisor Visits"
          subtitle="Site inspections logged by supervisors — findings, action items,  observed."
        />
      )}
      <div className={isFs ? "p-6 space-y-3" : "p-8 space-y-3"} data-testid="supervisor-visits-list">
        <StickyFiltersBar
          testIdPrefix="supervisor-visits"
          value={filters}
          onChange={setFilters}
          visibleCount={filtered.length}
          totalCount={rows.length}
          isFullscreen={isFs}
          fullscreenUrl={isFs ? null : "/supervisor-visits/fullscreen"}
          collapsible
        />

        <div className="border border-[#27272A] bg-[#121212]">
          <div className="px-5 py-4 border-b border-[#27272A]">
            <div className="label-mute">{filtered.length} visit{filtered.length === 1 ? "" : "s"} on record</div>
            <div className="font-display text-xl uppercase font-bold tracking-tight">Supervisor Ledger</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="supervisor-visits-table">
              <thead className="border-b border-[#27272A] text-left">
                <tr>
                  <SortableHeader k="visit_time" state={sortState}>When</SortableHeader>
                  <SortableHeader k="supervisor_name" state={sortState}>Supervisor</SortableHeader>
                  <SortableHeader k="site_name" state={sortState}>Site</SortableHeader>
                  <SortableHeader k="guards_count" state={sortState}>Officers</SortableHeader>
                  <SortableHeader k="findings_count" state={sortState}>Findings</SortableHeader>
                  <th className="px-4 py-3 text-right label-mute">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#27272A]">
                {sortState.sorted.map((v) => (
                  <tr key={v.id} className="hover:bg-[#1c1c1e]" data-testid={`visit-row-${v.id}`}>
                    <td className="px-4 py-3 font-mono text-xs text-zinc-400 whitespace-nowrap">
                      {new Date(v.visit_time).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-zinc-300 whitespace-nowrap">{v.supervisor_name}</td>
                    <td className="px-4 py-3 font-medium">{v.site_name || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs text-zinc-500">{(v.guards_observed || []).length}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1 max-w-md">
                        {(v.findings_tags || []).slice(0, 4).map((t) => <PillBadge key={t}>{t}</PillBadge>)}
                        {(v.findings_tags || []).length > 4 && <span className="text-xs text-zinc-500">+{v.findings_tags.length - 4}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <GhostButton data-testid={`view-visit-${v.id}`} onClick={() => setDetail(v)}><Eye className="size-3.5" /></GhostButton>
                        <GhostButton onClick={() => onDelete(v.id)}><Trash2 className="size-3.5" /></GhostButton>
                      </div>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={6} className="text-center py-16 text-zinc-500 scanlines">No supervisor visits recorded yet.</td></tr>}
                {rows.length > 0 && sortState.sorted.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-16 text-zinc-500 scanlines">No visits match the filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {detail && (
        <Modal title="Supervisor Visit" onClose={() => setDetail(null)} testId="visit-detail-modal">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="label-mute mb-1"><Calendar className="inline size-3 mr-1" />When</div>
                <div className="font-mono text-zinc-300">{new Date(detail.visit_time).toLocaleString()}</div>
              </div>
              <div>
                <div className="label-mute mb-1"><User className="inline size-3 mr-1" />Supervisor</div>
                <div className="text-zinc-300">{detail.supervisor_name}</div>
              </div>
              <div className="col-span-2">
                <div className="label-mute mb-1"><MapPin className="inline size-3 mr-1" />Site</div>
                <div className="text-zinc-300 font-medium">{detail.site_name || "—"}</div>
              </div>
            </div>
            <div>
              <div className="label-mute mb-1">Officers observed ({(detail.guards_observed || []).length})</div>
              <div className="font-mono text-xs text-zinc-400">{(detail.guards_observed || []).join(", ") || "—"}</div>
            </div>
            <div>
              <div className="label-mute mb-1">Findings</div>
              <div className="flex flex-wrap gap-1">{(detail.findings_tags || []).map((t) => <PillBadge key={t}>{t}</PillBadge>)}</div>
            </div>
            {detail.findings_text && (
              <div>
                <div className="label-mute mb-1">Observations</div>
                <div className="text-zinc-300 whitespace-pre-wrap border border-[#27272A] p-3 bg-[#0A0A0A]">{detail.findings_text}</div>
              </div>
            )}
            {detail.action_items && (
              <div>
                <div className="label-mute mb-1">Action items</div>
                <div className="text-zinc-300 whitespace-pre-wrap border border-[#27272A] p-3 bg-[#0A0A0A]">{detail.action_items}</div>
              </div>
            )}
            {(detail.photos || []).length > 0 && (
              <div>
                <div className="label-mute mb-2">Photos ({detail.photos.length})</div>
                <div className="grid grid-cols-3 gap-2">
                  {detail.photos.map((p, i) => (
                    <img key={(p && (p.url || p.storage_path)) || i} src={p.url || p} alt="" className="w-full h-32 object-cover border border-[#27272A]" />
                  ))}
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
